import { useEffect, useRef, useState, type FormEvent } from 'react';
import { PaperclipIcon, PlusIcon } from 'lucide-react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import type {
  AdminExpense,
  ExpenseAttachment,
  ExpensePaymentMethod,
  SaveExpenseRequest,
} from '@mds/shared/types/expense';

import { useSession } from '@/auth/use-session';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import EmptyState from '@/components/ui/empty-state';
import Field, { controlCls, FieldError, inputCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import Pager from '@/components/ui/pager';
import StatCard from '@/components/ui/stat-card';
import { Cell, Row, Table } from '@/components/ui/table';
import { Toolbar } from '@/components/ui/toolbar';
import * as expenses from '@/expenses/expenses.api';
import { checkFile, formatBytes, uploadFile } from '@/files/upload';
import { dateStamp } from '@mds/shared/lib/dates';
import { formatCents, inputToCents, centsToInput } from '@/lib/money';
import Select from '@mds/shared/ui/select';

const ALL = 'all';
const PAGE_SIZE = 25;
const METHODS: { value: ExpensePaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'whish', label: 'Whish' },
  { value: 'omt', label: 'OMT' },
  { value: 'other', label: 'Other' },
];

const today = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const displayDate = (value: string) => dateStamp(value);

interface Draft {
  expenseDate: string;
  title: string;
  reason: string;
  amount: string;
  method: ExpensePaymentMethod;
  methodDetail: string;
  attachments: ExpenseAttachment[];
}

const blankDraft = (): Draft => ({
  expenseDate: today(),
  title: '',
  reason: '',
  amount: '',
  method: 'cash',
  methodDetail: '',
  attachments: [],
});

const draftOf = (expense: AdminExpense): Draft => ({
  expenseDate: expense.expenseDate,
  title: expense.title,
  reason: expense.reason,
  amount: centsToInput(expense.amountMinor),
  method: expense.method,
  methodDetail: expense.methodDetail,
  attachments: expense.attachments,
});

function ExpenseEditor({
  expense,
  onClose,
}: {
  expense: AdminExpense | null;
  onClose: () => void;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Draft>(() => (expense ? draftOf(expense) : blankDraft()));
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState('');

  useEffect(() => setDraft(expense ? draftOf(expense) : blankDraft()), [expense]);

  const save = useMutation({
    mutationFn: (body: SaveExpenseRequest) =>
      expense
        ? expenses.updateExpense(expense.expenseId, {
            ...body,
            expectedVersionNumber: expense.versionNumber,
          })
        : expenses.createExpense(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      onClose();
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'The expense could not be saved.'),
  });

  const addFiles = async (chosen: File[]) => {
    if (draft.attachments.length + chosen.length > 10) {
      setError('An expense can have at most 10 attachments.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      for (const file of chosen) {
        const problem = checkFile(file);
        if (problem) throw new Error(problem);
        const stored = await uploadFile(file, 'private', (phase) =>
          setUploadNote(`${phase} ${file.name}`),
        );
        setDraft((current) => ({
          ...current,
          attachments: [
            ...current.attachments,
            {
              fileId: stored.id,
              originalFilename: stored.originalFilename,
              mimeType: stored.mimeType,
              sizeBytes: stored.sizeBytes,
              displayOrder: current.attachments.length,
            },
          ],
        }));
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : (cause as Error).message);
    } finally {
      setUploading(false);
      setUploadNote('');
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const amountMinor = inputToCents(draft.amount);
    if (!amountMinor || amountMinor <= 0) {
      setError('Enter an amount greater than zero.');
      return;
    }
    if (!draft.title.trim() || !draft.reason.trim()) {
      setError('Title and reason are required.');
      return;
    }
    if (draft.method === 'other' && !draft.methodDetail.trim()) {
      setError('Type the payment method used.');
      return;
    }
    save.mutate({
      expenseDate: draft.expenseDate,
      title: draft.title.trim(),
      reason: draft.reason.trim(),
      amountMinor,
      method: draft.method,
      ...(draft.method === 'other' ? { methodDetail: draft.methodDetail.trim() } : {}),
      attachmentFileIds: draft.attachments.map((attachment) => attachment.fileId),
    });
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {expense && (
        <p className="m-0 rounded-xl border border-hairline bg-raised px-4 py-3 text-base text-ink-muted">
          Saving appends version {expense.versionNumber + 1}. Version {expense.versionNumber}{' '}
          remains unchanged.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Expense date" required>
          <input
            type="date"
            required
            value={draft.expenseDate}
            onChange={(event) =>
              setDraft((value) => ({ ...value, expenseDate: event.target.value }))
            }
            className={inputCls}
          />
        </Field>
        <Field label="Amount (USD)" required hint="Stored as exact USD cents.">
          <input
            inputMode="decimal"
            required
            placeholder="0.00"
            value={draft.amount}
            onChange={(event) => setDraft((value) => ({ ...value, amount: event.target.value }))}
            className={inputCls}
          />
        </Field>
      </div>
      <Field label="Expense title" required>
        <input
          required
          maxLength={160}
          value={draft.title}
          onChange={(event) => setDraft((value) => ({ ...value, title: event.target.value }))}
          className={inputCls}
        />
      </Field>
      <Field label="Reason" required>
        <textarea
          required
          rows={4}
          maxLength={2000}
          value={draft.reason}
          onChange={(event) => setDraft((value) => ({ ...value, reason: event.target.value }))}
          className={inputCls}
        />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Payment method" required>
          <Select
            variant="field"
            value={draft.method}
            onChange={(method) =>
              setDraft((value) => ({
                ...value,
                method: method as ExpensePaymentMethod,
                methodDetail: method === 'other' ? value.methodDetail : '',
              }))
            }
            options={METHODS}
          />
        </Field>
        {draft.method === 'other' && (
          <Field label="Other method" required>
            <input
              required
              maxLength={80}
              value={draft.methodDetail}
              onChange={(event) =>
                setDraft((value) => ({ ...value, methodDetail: event.target.value }))
              }
              className={inputCls}
            />
          </Field>
        )}
      </div>
      <Field
        label="Receipts and supporting documents"
        hint="Private JPEG, PNG, WebP or PDF. Up to 10 files."
      >
        <div className="flex flex-col gap-3 rounded-xl border border-hairline bg-raised p-4">
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(event) => {
              void addFiles([...(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />
          <Button
            variant="quiet"
            disabled={uploading || draft.attachments.length >= 10}
            onClick={() => fileInput.current?.click()}
          >
            <PaperclipIcon className="size-4" />{' '}
            {uploading ? uploadNote || 'Uploading…' : 'Attach files'}
          </Button>
          {draft.attachments.map((attachment) => (
            <div
              key={attachment.fileId}
              className="flex items-center justify-between gap-3 text-base"
            >
              <span className="min-w-0 truncate">
                {attachment.originalFilename} · {formatBytes(attachment.sizeBytes)}
              </span>
              <button
                type="button"
                className="text-danger-text underline"
                onClick={() =>
                  setDraft((value) => ({
                    ...value,
                    attachments: value.attachments.filter(
                      (item) => item.fileId !== attachment.fileId,
                    ),
                  }))
                }
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </Field>
      {error && <FieldError>{error}</FieldError>}
      <div className="flex gap-3">
        <Button type="submit" disabled={save.isPending || uploading || !can('expenses.manage')}>
          {save.isPending ? 'Saving…' : expense ? 'Save new version' : 'Record expense'}
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ExpenseHistory({ expense, onClose }: { expense: AdminExpense; onClose: () => void }) {
  const history = useQuery({
    queryKey: ['expenses', expense.expenseId, 'versions'],
    queryFn: () => expenses.listVersions(expense.expenseId, 1, 100),
  });

  const download = async (attachment: ExpenseAttachment) => {
    const result = await expenses.attachmentDownloadUrl(expense.expenseId, attachment.fileId);
    window.open(result.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal open onClose={onClose} title={`${expense.reference} history`}>
      {history.isPending ? (
        <p className="m-0">Loading history…</p>
      ) : history.isError ? (
        <FieldError>Version history could not be loaded.</FieldError>
      ) : (
        <div className="flex flex-col gap-4">
          {(history.data?.data ?? []).map((version) => (
            <article key={version.id} className="rounded-xl border border-hairline bg-raised p-4">
              <div className="flex flex-wrap justify-between gap-3">
                <strong>
                  Version {version.versionNumber}
                  {version.versionNumber === expense.versionNumber ? ' · current' : ''}
                </strong>
                <span>
                  {displayDate(version.expenseDate)} · {formatCents(version.amountMinor)}
                </span>
              </div>
              <p className="mt-3 mb-1 font-semibold">{version.title}</p>
              <p className="m-0 whitespace-pre-wrap text-ink-muted">{version.reason}</p>
              <p className="mt-3 mb-0 text-base text-ink-muted">
                {version.methodLabel} · recorded {new Date(version.createdAt).toLocaleString()}
              </p>
              {version.attachments.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {version.attachments.map((attachment) => (
                    <Button
                      key={attachment.fileId}
                      variant="quiet"
                      onClick={() => void download(attachment)}
                    >
                      <PaperclipIcon className="size-4" /> {attachment.originalFilename}
                    </Button>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}

export default function ExpensesPage() {
  const { can } = useSession();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [method, setMethod] = useState(ALL);
  const [editing, setEditing] = useState<AdminExpense | null | undefined>(undefined);
  const [history, setHistory] = useState<AdminExpense | null>(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const list = useQuery({
    queryKey: ['expenses', { page, search, method }],
    queryFn: () =>
      expenses.listExpenses({
        page,
        limit: PAGE_SIZE,
        q: search || undefined,
        method: method === ALL ? undefined : (method as ExpensePaymentMethod),
      }),
  });

  if (list.isError) {
    return (
      <EmptyState
        title="Could not load expenses"
        hint={
          list.error instanceof ApiError ? list.error.message : 'The server could not be reached.'
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <StatCard
          label="Expenses (filtered)"
          value={list.data ? formatCents(list.data.summary.totalAmountMinor) : '—'}
          note={list.data ? `${list.data.summary.expenseCount} records` : 'Loading'}
          density="compact"
        />
        <StatCard
          label="History policy"
          value="Versioned"
          note="Edits append; records cannot be voided or deleted"
          density="compact"
        />
      </div>
      <Card
        title="Expense ledger"
        action={
          <Toolbar>
            <input
              value={q}
              maxLength={160}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Reference, title or reason"
              className={controlCls + ' w-[260px]'}
            />
            <Select
              value={method}
              onChange={(value) => {
                setMethod(value);
                setPage(1);
              }}
              options={[{ value: ALL, label: 'All methods' }, ...METHODS]}
            />
            {can('expenses.manage') && (
              <Button onClick={() => setEditing(null)}>
                <PlusIcon className="size-4" /> Add expense
              </Button>
            )}
          </Toolbar>
        }
      >
        {list.isPending ? (
          <p className="m-0 text-ink-muted">Loading expenses…</p>
        ) : list.data?.data.length === 0 ? (
          <EmptyState
            title="No expenses found"
            hint="Record the first expense or change the filters."
          />
        ) : (
          <>
            <Table head={['Reference', 'Date', 'Title', 'Method', 'Amount', 'Version', 'Actions']}>
              {list.data?.data.map((expense) => (
                <Row key={expense.expenseId}>
                  <Cell className="font-mono">{expense.reference}</Cell>
                  <Cell>{displayDate(expense.expenseDate)}</Cell>
                  <Cell>
                    <span className="font-semibold">{expense.title}</span>
                    <span className="block max-w-[280px] truncate text-sm text-ink-muted">
                      {expense.reason}
                    </span>
                  </Cell>
                  <Cell>{expense.methodLabel}</Cell>
                  <Cell className="font-semibold">{formatCents(expense.amountMinor)}</Cell>
                  <Cell>v{expense.versionNumber}</Cell>
                  <Cell>
                    <div className="flex gap-2">
                      <Button variant="quiet" onClick={() => setHistory(expense)}>
                        History
                      </Button>
                      {can('expenses.manage') && (
                        <Button variant="quiet" onClick={() => setEditing(expense)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  </Cell>
                </Row>
              ))}
            </Table>
            <Pager pagination={list.data?.pagination} onPage={setPage} />
          </>
        )}
      </Card>
      {editing !== undefined && (
        <Modal
          open
          onClose={() => setEditing(undefined)}
          title={editing ? `Edit ${editing.reference}` : 'Record expense'}
        >
          <ExpenseEditor expense={editing} onClose={() => setEditing(undefined)} />
        </Modal>
      )}
      {history && <ExpenseHistory expense={history} onClose={() => setHistory(null)} />}
    </div>
  );
}
